import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import api from "../../components/utils/api"
import MainTitle from "../../components/template/MainTitle"
import SubTitle from "../../components/template/SubTitle"
import ExperimentStatusPill from "./ExperimentStatusPill"
import ButtonWrap from "../../components/template/ButtonWrap"
import Spinner from "../../components/template/Spinner"
import Modal from "../../components/template/Modal"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faArrowRight, faArrowRotateRight, faChevronDown, faChevronRight, faList, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons"
import TraceStepper from "../../components/template/TraceStepper"
import { PREACTION_OPTIONS } from "./preactionOptions"
import TrainingTriggerModal from "./TrainingTriggerModal"
import TrainingList from "./TrainingList"
import { BASE_EXP_VER } from "./trainingOptions"

const OptionCard = ({ label, description, value, onView, viewDisabled = false }) => (
    <div className="border rounded-lg px-4 py-3 text-left grid grid-cols-[1fr_7rem] items-center gap-3">
        <div className="min-w-0">
            <div className="text-sm text-gray-700">{label}</div>
            {description && <div className="text-xs text-gray-400 mt-0.5">{description}</div>}
        </div>
        <div className="flex items-center justify-end gap-3">
            <span className="text-sm text-gray-500">{value}</span>
            {onView && <button
                onClick={onView}
                disabled={viewDisabled}
                title="View classes"
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:hover:text-gray-400"
            ><FontAwesomeIcon icon={faList} /></button>}
        </div>
    </div>
)

const ClassListModal = ({ title, classes, isLoading, isError }) => (
    <div>
        <MainTitle title={title} />
        {isLoading
            ? <div className="my-3"><Spinner /></div>
            : isError
            ? <div className="my-3 text-sm text-red-500">Failed to load classes. Please try again.</div>
            : classes.length === 0
            ? <div className="my-3 text-sm text-gray-500">No classes.</div>
            : <div className="md:grid md:grid-cols-3 mt-4 text-left">
                {classes.map((className, idx) => <div key={idx} className="px-3 py-1 text-sm">{className}</div>)}
            </div>}
    </div>
)

// Models of this experiment that were trained outside the webview; the ones
// it triggered itself are listed in the Trainings section instead.
const ExternalModelList = ({ models, isLoading }) => (
    <div className="mt-8">
        <SubTitle title="External Models" />
        {isLoading
            ? <div className="my-3"><Spinner /></div>
            : models.length === 0
            ? <div className="my-3 text-sm text-gray-500">No externally trained models.</div>
            : <div className="my-3 border rounded-lg divide-y">
                {models.map(model => (
                    <div key={model.model_name} className="flex justify-between items-center px-4 py-3">
                        <span className="text-sm text-gray-700">{model.model_name}</span>
                        <a
                            href={`/training-progress/models/${encodeURIComponent(model.model_name)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Show training report"
                            className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-gray-600"
                        >Show report <FontAwesomeIcon icon={faArrowRight} /></a>
                    </div>
                ))}
            </div>}
    </div>
)

const AdvancedOptions = ({ options, onViewDefaultFilter, onViewExcluded, onViewIncluded }) => {
    // whitelist mode is mutually exclusive with the default-filter/excluded mode
    const isWhitelist = options.included_classes?.length > 0
    return (
        <div className="mt-8">
            <SubTitle title="Advanced Options" />
            <div className="my-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                {isWhitelist
                    ? <OptionCard
                        label={PREACTION_OPTIONS.included_classes.label}
                        description={PREACTION_OPTIONS.included_classes.description.view}
                        value={`${options.included_classes.length} classes`}
                        onView={onViewIncluded}
                    />
                    : <>
                        <OptionCard
                            label={PREACTION_OPTIONS.default_filter.label}
                            description={PREACTION_OPTIONS.default_filter.description.view}
                            value={<FontAwesomeIcon icon={options.include_default_filter ? faCheck : faXmark} />}
                            onView={onViewDefaultFilter}
                        />
                        <OptionCard
                            label={PREACTION_OPTIONS.excluded_classes.label}
                            description={PREACTION_OPTIONS.excluded_classes.description.view}
                            value={options.excluded_classes.length > 0 ? `${options.excluded_classes.length} classes` : "None"}
                            onView={onViewExcluded}
                            viewDisabled={options.excluded_classes.length === 0}
                        />
                    </>}
                <OptionCard
                    label={PREACTION_OPTIONS.min_samples.label}
                    description={PREACTION_OPTIONS.min_samples.description.view}
                    value={options.min_sample}
                />
                <OptionCard
                    label={PREACTION_OPTIONS.max_samples.label}
                    description={PREACTION_OPTIONS.max_samples.description.view}
                    value={options.max_sample}
                />
            </div>
        </div>
    )
}

const ExperimentDetail = () => {
    const [experiment, setExperiment] = useState({})
    const [traceCode, setTraceCode] = useState(201)
    const [models, setModels] = useState([])
    const [isModelsLoading, setIsModelsLoading] = useState(true)
    const [preactionOptions, setPreactionOptions] = useState(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [modalTitle, setModalTitle] = useState("")
    const [modalClasses, setModalClasses] = useState([])
    const [isModalLoading, setIsModalLoading] = useState(false)
    const [isModalError, setIsModalError] = useState(false)
    const [trainModalOpen, setTrainModalOpen] = useState(false)
    // null until the user toggles; the default then follows the preaction state
    const [progressOpen, setProgressOpen] = useState(null)
    const [trainingsRefreshKey, setTrainingsRefreshKey] = useState(0)
    const [queuedNotice, setQueuedNotice] = useState("")
    // result-folder names of the webview-triggered trainings (null until the
    // trainings list loads); models outside this set were trained externally
    const [trainingFolders, setTrainingFolders] = useState(null)

    const params = useParams()
    const navigate = useNavigate()

    const fetchTrace = useCallback(async () => {
        try {
            const res = await api.get(`/api/experiments/${params.id}/traces`)
            setTraceCode(res.data.experiment_traces.length > 0 ? res.data.experiment_traces[0].trace_code : 200)
        } catch (error) {
            console.error(error)
        }
    }, [params])

    const fetchModels = useCallback(async () => {
        setIsModelsLoading(true)
        try {
            const res = await api.get(`/api/experiments/${params.id}/models`)
            setModels(res.data.models)
        } catch (error) {
            console.error(error)
        } finally {
            setIsModelsLoading(false)
        }
    }, [params])

    const fetchPreactionOptions = useCallback(async () => {
        try {
            const res = await api.get(`/api/experiments/${params.id}/preaction-options`)
            setPreactionOptions(res.data)
        } catch (error) {
            console.error(error)
        }
    }, [params])

    useEffect(() => { fetchModels() }, [fetchModels])
    useEffect(() => { fetchPreactionOptions() }, [fetchPreactionOptions])

    const fetchDefaultFilter = useCallback(async () => {
        const res = await api.get(`/api/dbs/${experiment.database_summary.id}/default-filter`)
        return res.data.classes
    }, [experiment])

    // Opens the class-list modal. `source` is either a ready array of class
    // names or an async fetcher that resolves to one (shows a spinner while loading).
    const showClassesModal = async (title, source) => {
        setModalTitle(title)
        setModalOpen(true)
        setIsModalError(false)
        if (typeof source !== "function") {
            setModalClasses(source)
            setIsModalLoading(false)
            return
        }
        setModalClasses([])
        setIsModalLoading(true)
        try {
            setModalClasses(await source())
        } catch (error) {
            console.error(error)
            setIsModalError(true)
        } finally {
            setIsModalLoading(false)
        }
    }

    const handleTrainingsLoaded = useCallback((folders) => setTrainingFolders(folders), [])

    // expanded by default while preaction is still running, collapsed once done
    const isProgressOpen = progressOpen ?? experiment.status?.value === 0

    // mirrors the trigger endpoint's gate: an experiment still in preaction
    // has nothing to train yet, and v0 carries the database's base model
    const isBaseExperiment = experiment.version === BASE_EXP_VER
    const canTrain = experiment.status?.value !== 0 && !isBaseExperiment

    // the Trainings section shows every webview-triggered training, so the
    // models section only lists what was trained outside the webview
    const externalModels = useMemo(
        () => trainingFolders === null ? null : models.filter(m => !trainingFolders.has(m.model_name)),
        [models, trainingFolders],
    )

    const fetchExperiment = useCallback(async () => {
        try {
            const res = await api.get(`/api/experiments/${params.id}`)
            setExperiment(res.data)
        } catch (error) {
            console.error(error)
        }
    }, [params])

    useEffect(() => {
        fetchExperiment()
        fetchTrace()
    }, [fetchExperiment, fetchTrace])

    const defaultSteps = useMemo(() => [
        { id: 0, title: 'Preaction process started', stepStatus: 'waiting', subSteps: [] },
        {
            id: 1, title: 'Resource preparation', stepStatus: 'waiting', subSteps: [
                { id: 0, message: 'Resources preparation started', subStepStatus: 'waiting' },
                { id: 1, message: 'Get CochlDB and Cochlogy version', subStepStatus: 'waiting' },
                { id: 2, message: 'Get class hierarchy', subStepStatus: 'waiting' },
                { id: 3, message: 'Get class filter', subStepStatus: 'waiting' },
                { id: 4, message: 'Get benchmark merge rule', subStepStatus: 'waiting' },
                { id: 5, message: 'Get CochlDB records', subStepStatus: 'waiting' },
                { id: 9, message: 'Resources preparation completed', subStepStatus: 'waiting' },
            ]
        },
        {
            id: 2, title: 'Data table creation', stepStatus: 'waiting', subSteps: [
                { id: 0, message: 'Data table creation started', subStepStatus: 'waiting' },
                { id: 1, message: 'Exclude classes with insufficient data', subStepStatus: 'waiting' },
                { id: 2, message: 'Limit class data to ensure balance', subStepStatus: 'waiting' },
                { id: 3, message: 'Apply class hierarchy and filter', subStepStatus: 'waiting' },
                { id: 4, message: 'Apply benchmark merge rules', subStepStatus: 'waiting' },
                { id: 5, message: 'Data table creation', subStepStatus: 'waiting' },
                { id: 9, message: 'Data table creation completed', subStepStatus: 'waiting' },
            ]
        },
        { id: 9, title: 'Preaction process completed', stepStatus: 'waiting', subSteps: [] },
    ], [])

    return (
        <div className='default-background'>
            {experiment.hasOwnProperty('id') && <div>
                <div className="flex justify-between items-center my-5">
                    <div className="flex items-center">
                        <MainTitle title={`${experiment.database_summary.name}_${experiment.version}`} />
                        <div className="mx-3"><ExperimentStatusPill value={experiment.status.name} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 items-center">
                        {experiment.status.value === 0 && <ButtonWrap contents={<FontAwesomeIcon icon={faArrowRotateRight} />} onClick={() => { fetchTrace(); fetchModels() }} />}
                        <ButtonWrap contents={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate('/experiments')} />
                    </div>
                </div>
                <div className="mt-8">
                    <button
                        type="button"
                        className="flex items-center gap-2"
                        onClick={() => setProgressOpen(!isProgressOpen)}
                    >
                        <SubTitle title="Creation Progress" />
                        <FontAwesomeIcon icon={isProgressOpen ? faChevronDown : faChevronRight} className="text-gray-400" />
                    </button>
                    {isProgressOpen && <TraceStepper defaultSteps={defaultSteps} traceCode={experiment.status.value === 0 ? traceCode : 299} />}
                </div>
                <TrainingList
                    experimentId={params.id}
                    refreshKey={trainingsRefreshKey}
                    onTrain={canTrain ? () => setTrainModalOpen(true) : undefined}
                    disabledReason={isBaseExperiment
                        ? `${BASE_EXP_VER} is reserved for the database's base model, which is trained automatically on release. To train your own models, create another experiment.`
                        : undefined}
                    onLoaded={handleTrainingsLoaded}
                    notice={queuedNotice}
                    onDismissNotice={() => setQueuedNotice("")}
                />
                <ExternalModelList
                    models={externalModels || []}
                    isLoading={isModelsLoading || externalModels === null}
                />
                {preactionOptions && <AdvancedOptions
                    options={preactionOptions}
                    onViewExcluded={() => showClassesModal("Custom Excluded Classes", preactionOptions.excluded_classes)}
                    onViewDefaultFilter={() => showClassesModal("Default Class Filter", fetchDefaultFilter)}
                    onViewIncluded={() => showClassesModal("Included Classes", preactionOptions.included_classes)}
                />}
            </div>}
            {modalOpen && <Modal
                visible={modalOpen}
                closable={true}
                maskClosable={true}
                onClose={() => setModalOpen(false)}>{
                    <ClassListModal title={modalTitle} classes={modalClasses} isLoading={isModalLoading} isError={isModalError} />
                }</Modal>}
            {trainModalOpen && <Modal
                visible={trainModalOpen}
                closable={true}
                maskClosable={true}
                onClose={() => setTrainModalOpen(false)}>{
                    <TrainingTriggerModal
                        experiment={experiment}
                        models={models}
                        closeModal={() => setTrainModalOpen(false)}
                        onTriggered={(data) => {
                            setTrainingsRefreshKey(k => k + 1)
                            const model = `${data.training.architecture} · ${data.training.sample_rate}`
                            setQueuedNotice(data.gpu_jobs_ahead > 0
                                ? `${model} training submitted. ${data.gpu_jobs_ahead} GPU job(s) are ahead of it in the queue.`
                                : `${model} training submitted. It will start right away.`)
                        }}
                    />
                }</Modal>}
        </div>
    )
}

export default ExperimentDetail